import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Calendar, Rocket, TrendingUp, Award, Users, Target } from 'lucide-react';
import NavigationControls from '../components/NavigationControls';
import ProgressBar from '../components/ProgressBar';
import PartieBadge from '../components/PartieBadge';
import { useLanguage } from '../contexts/LanguageContext';
import { useScenario } from '../contexts/ScenarioContext';
import ScenarioToggle from '../components/ScenarioToggle';
import { previsionsY1Minimum, previsionsY1Realiste, objectifsY1 } from '../mock';

function getRoadmap6Months(language, scenario) {
  const data = scenario === 'minimum' ? previsionsY1Minimum : previsionsY1Realiste;
  const phases = [
    {
      mois: 'M1 — Septembre',
      moisEn: 'M1 — September',
      icon: Rocket,
      color: 'lime',
      ecoles: data[0].ecoles,
      garderies: data[0].garderies,
      repas: data[0].repasJour,
      highlights: language === 'fr' ? [
        `Activation équipe (Dan, Rox, 3 vendeurs, Top Closer, MC/Phil)`,
        `Démarrage cadence 15 traiteurs/mois`,
        `Base : ${data[0].traiteurs} traiteurs, ${data[0].etablissements} établissements`,
      ] : [
        `Team activation (Dan, Rox, 3 reps, Top Closer, MC/Phil)`,
        `Start 15 caterers/month cadence`,
        `Base: ${data[0].traiteurs} caterers, ${data[0].etablissements} institutions`,
      ],
    },
    {
      mois: 'M2 — Octobre',
      moisEn: 'M2 — October',
      icon: TrendingUp,
      color: 'cyan',
      ecoles: data[1].ecoles,
      garderies: data[1].garderies,
      repas: data[1].repasJour,
      highlights: language === 'fr' ? [
        `Activation abonnement parent 2$/mois (50% don écoles)`,
        `Premier produit-market fit validé avec données réelles`,
        `${data[1].traiteurs} traiteurs, ${data[1].etablissements} établissements`,
      ] : [
        `Parent subscription $2/month activation (50% school donation)`,
        `First product-market fit validated with real data`,
        `${data[1].traiteurs} caterers, ${data[1].etablissements} institutions`,
      ],
    },
    {
      mois: 'M3 — Novembre',
      moisEn: 'M3 — November',
      icon: TrendingUp,
      color: 'cyan',
      ecoles: data[2].ecoles,
      garderies: data[2].garderies,
      repas: data[2].repasJour,
      highlights: language === 'fr' ? [
        `Activation Sezzle BNPL (Tier 1)`,
        `Cadence pleine, scaling commercial`,
        `${data[2].traiteurs} traiteurs, ${data[2].etablissements} établissements`,
      ] : [
        `Sezzle BNPL activation (Tier 1)`,
        `Full cadence, commercial scaling`,
        `${data[2].traiteurs} caterers, ${data[2].etablissements} institutions`,
      ],
    },
    {
      mois: 'M4 — Décembre',
      moisEn: 'M4 — December',
      icon: Award,
      color: 'orange',
      ecoles: data[3].ecoles,
      garderies: data[3].garderies,
      repas: data[3].repasJour,
      highlights: language === 'fr' ? [
        `Break-even mensuel approché (rev ~${(data[3].revenuMensuel/1000).toFixed(0)}K $)`,
        `Préparation transition Tier 1 → Tier 2`,
        `${data[3].traiteurs} traiteurs, ${data[3].etablissements} établissements`,
      ] : [
        `Monthly break-even approached (rev ~$${(data[3].revenuMensuel/1000).toFixed(0)}K)`,
        `Tier 1 → Tier 2 transition prep`,
        `${data[3].traiteurs} caterers, ${data[3].etablissements} institutions`,
      ],
    },
    {
      mois: 'M5 — Janvier',
      moisEn: 'M5 — January',
      icon: Users,
      color: 'purple',
      ecoles: data[4].ecoles,
      garderies: data[4].garderies,
      repas: data[4].repasJour,
      highlights: language === 'fr' ? [
        `Premier mois positif (résultat +${(data[4].resultat/1000).toFixed(0)}K $)`,
        `Préparation matériaux investisseurs (data room)`,
        `${data[4].traiteurs} traiteurs, ${data[4].etablissements} établissements`,
      ] : [
        `First positive month (result +$${(data[4].resultat/1000).toFixed(0)}K)`,
        `Investor materials prep (data room)`,
        `${data[4].traiteurs} caterers, ${data[4].etablissements} institutions`,
      ],
    },
    {
      mois: 'M6 — Février — Pivot Stratégique',
      moisEn: 'M6 — February — Strategic Pivot',
      icon: Target,
      color: 'lime',
      ecoles: data[5].ecoles,
      garderies: data[5].garderies,
      repas: data[5].repasJour,
      highlights: language === 'fr' ? [
        `Objectifs atteints : ${data[5].ecoles} écoles + ${data[5].garderies} garderies`,
        `${data[5].repasJour.toLocaleString()} repas/jour • ${(data[5].gmvAnnualise/1000000).toFixed(1)}M $ GMV`,
        `🚀 Lancement ronde seed (~10% de la valeur établie)`,
      ] : [
        `Targets reached: ${data[5].ecoles} schools + ${data[5].garderies} daycares`,
        `${data[5].repasJour.toLocaleString()} meals/day • $${(data[5].gmvAnnualise/1000000).toFixed(1)}M GMV`,
        `🚀 Seed round launch (~10% of established value)`,
      ],
    },
  ];
  return phases;
}

export default function RoadmapPage() {
  const { language } = useLanguage();
  const { scenario } = useScenario();
  const phases = getRoadmap6Months(language, scenario);
  const maxRepas = Math.max(...phases.map(p => p.repas));
  const obj = scenario === 'minimum' ? objectifsY1.minimum : objectifsY1.realiste;

  return (
    <div className="min-h-screen p-4 sm:p-6 lg:p-8">
      <div className="max-w-5xl mx-auto">
        <PartieBadge path="/roadmap" />
        <Card className="bg-black/60 border-lime-500/30 backdrop-blur-sm">
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <CardTitle className="text-2xl sm:text-3xl lg:text-4xl text-white mb-2">
                  {language === 'fr' ? 'Roadmap 6 Mois — Pré-seed' : '6-Month Pre-seed Roadmap'}
                </CardTitle>
                <div className="h-1 w-16 sm:w-24 bg-gradient-to-r from-lime-500 to-cyan-500 rounded-full" />
              </div>
              <ScenarioToggle />
            </div>
          </CardHeader>
          <CardContent className="space-y-8">
            <p className="text-gray-300 text-base leading-relaxed">
              {language === 'fr'
                ? 'Roadmap d\'exécution du pré-seed 500K — Septembre à Février. Objectifs atteints à M6 déclenchent la ronde seed. La suite (M7-M12) dépendra du capital obtenu.'
                : 'Pre-seed $500K execution roadmap — September to February. M6 targets trigger the seed round. Beyond (M7-M12) depends on capital raised.'}
            </p>

            {/* Phases */}
            <div className="space-y-6">
              {phases.map((phase, idx) => {
                const Icon = phase.icon;
                return (
                  <div key={idx} className={`relative pl-8 border-l-2 border-${phase.color}-500/50`}>
                    <div className={`absolute -left-3 top-0 w-6 h-6 rounded-full bg-${phase.color}-500 flex items-center justify-center`}>
                      <Icon className="h-3 w-3 text-black" />
                    </div>
                    <Card className={`bg-gradient-to-br from-${phase.color}-500/10 to-transparent border-${phase.color}-500/30`}>
                      <CardHeader className="pb-2">
                        <CardTitle className={`text-lg text-${phase.color}-400`}>
                          {language === 'fr' ? phase.mois : phase.moisEn}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div className="p-2 bg-black/30 rounded-lg">
                            <p className="text-xs text-gray-400">{language === 'fr' ? 'Écoles' : 'Schools'}</p>
                            <p className={`text-xl font-bold text-${phase.color}-400`}>
                              {phase.ecoles.toLocaleString(language === 'fr' ? 'fr-CA' : 'en-CA')}
                            </p>
                          </div>
                          <div className="p-2 bg-black/30 rounded-lg">
                            <p className="text-xs text-gray-400">{language === 'fr' ? 'Garderies' : 'Daycares'}</p>
                            <p className={`text-xl font-bold text-${phase.color}-400`}>
                              {phase.garderies.toLocaleString(language === 'fr' ? 'fr-CA' : 'en-CA')}
                            </p>
                          </div>
                          <div className="p-2 bg-black/30 rounded-lg">
                            <p className="text-xs text-gray-400">{language === 'fr' ? 'Repas/jour' : 'Meals/day'}</p>
                            <p className={`text-xl font-bold text-${phase.color}-400`}>
                              {phase.repas.toLocaleString(language === 'fr' ? 'fr-CA' : 'en-CA')}
                            </p>
                          </div>
                        </div>

                        {maxRepas > 0 && (
                          <ProgressBar
                            value={phase.repas}
                            max={maxRepas}
                            label={`${Math.round(phase.repas / maxRepas * 100)}%`}
                            color={phase.color}
                          />
                        )}

                        <ul className="space-y-1">
                          {phase.highlights.map((h, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                              <span className={`text-${phase.color}-500 mt-0.5`}>•</span>
                              <span>{h}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  </div>
                );
              })}
            </div>

            {/* Résumé 6 mois */}
            <div className="p-6 bg-gradient-to-r from-lime-500/10 via-cyan-500/10 to-orange-500/10 rounded-xl border-2 border-lime-500/40">
              <h3 className="text-xl font-bold text-white mb-4 text-center">
                {language === 'fr' ? `Résumé M6 — Scénario ${obj.label}` : `M6 Summary — ${obj.label} Scenario`}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div>
                  <p className="text-sm text-gray-400">{language === 'fr' ? 'Écoles M6' : 'Schools M6'}</p>
                  <p className="text-2xl font-bold text-lime-400">{obj.m6.ecoles.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">{language === 'fr' ? 'Garderies M6' : 'Daycares M6'}</p>
                  <p className="text-2xl font-bold text-cyan-400">{obj.m6.garderies.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">{language === 'fr' ? 'Repas/jour M6' : 'Meals/day M6'}</p>
                  <p className="text-2xl font-bold text-orange-400">{obj.m6.repasJour.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">{language === 'fr' ? 'GMV annualisé' : 'Annualized GMV'}</p>
                  <p className="text-2xl font-bold text-lime-400">{(obj.m6.gmvAnnualise / 1000000).toFixed(0)}M $</p>
                </div>
              </div>
              <p className="text-xs text-gray-400 text-center mt-4 pt-3 border-t border-lime-500/20">
                {language === 'fr'
                  ? 'Au-delà de Février : croissance Min vers 1 000+1 000 ou Réaliste vers 1 500+1 500 établissements à M12, conditionnellement au succès de la ronde seed.'
                  : 'Beyond February: growth Min to 1,000+1,000 or Realistic to 1,500+1,500 institutions at M12, conditional on the seed round success.'}
              </p>
            </div>

            <NavigationControls />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
